import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import i18next from "i18next";
import { PageBody, PageHeader } from "components";
import orginizationService from "services/orginization-profile.service";
import { selectOrginizationDetails } from "redux/slices/orginizationSlice";
import { isAdminAccount } from "redux/slices/accountSlice";
import { useSelector } from "react-redux";
import css from "./tenantContent.module.scss";

const CONTENT_TYPES = {
  modules: { feature: "modules", label: "modules" },
  pathways: { feature: "pathways", label: "pathways" },
  programs: { feature: "programs", label: "programs" },
};

export default function TenantContentPage() {
  const { contentType } = useParams();
  const organization = useSelector(selectOrginizationDetails);
  const isAdmin = useSelector(isAdminAccount);
  const content = CONTENT_TYPES[contentType];
  const enabled =
    Boolean(content) &&
    Boolean(organization?.features?.content_management) &&
    Boolean(organization?.features?.[content.feature]);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedAvailableIds, setSelectedAvailableIds] = useState([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [availableSearch, setAvailableSearch] = useState("");
  const [loading, setLoading] = useState(enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  useEffect(() => {
    let active = true;
    if (!enabled) {
      setAvailableItems([]);
      setSelectedItems([]);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError("");
    setSaveError("");
    setSaveMessage("");
    orginizationService
      .getTenantContent(contentType)
      .then((response) => {
        if (!active) return;
        setAvailableItems(
          Array.isArray(response?.availableItems) ? response.availableItems : []
        );
        setSelectedItems(
          Array.isArray(response?.selectedItems) ? response.selectedItems : []
        );
        setSelectedAvailableIds([]);
        setSelectedTenantIds([]);
      })
      .catch((requestError) => {
        if (active) {
          setError(
            requestError.response?.data?.message ||
              i18next.t("labels.content_load_failed")
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [contentType, enabled]);

  const availableSelection = useMemo(
    () => new Set(selectedAvailableIds),
    [selectedAvailableIds]
  );
  const tenantSelection = useMemo(
    () => new Set(selectedTenantIds),
    [selectedTenantIds]
  );
  const filteredAvailableItems = useMemo(() => {
    const query = availableSearch.trim().toLocaleLowerCase();
    return query
      ? availableItems.filter((item) =>
          String(item.name).toLocaleLowerCase().includes(query)
        )
      : availableItems;
  }, [availableItems, availableSearch]);

  const moveItems = (source, ids) => {
    const movingIds = new Set(ids);
    if (source === "available") {
      const moving = availableItems.filter((item) => movingIds.has(Number(item.id)));
      setAvailableItems((items) =>
        items.filter((item) => !movingIds.has(Number(item.id)))
      );
      setSelectedItems((items) => [...items, ...moving]);
      setSelectedAvailableIds([]);
    } else {
      const moving = selectedItems.filter((item) => movingIds.has(Number(item.id)));
      setSelectedItems((items) =>
        items.filter((item) => !movingIds.has(Number(item.id)))
      );
      setAvailableItems((items) => [...items, ...moving]);
      setSelectedTenantIds([]);
    }
    setSaveMessage("");
    setSaveError("");
  };

  const moveDraggedItem = (itemId, target) => {
    const source = availableItems.some((item) => Number(item.id) === itemId)
      ? "available"
      : "selected";
    if (source !== target) moveItems(source, [itemId]);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaveMessage("");
    try {
      const response = await orginizationService.saveTenantContentSelection(
        contentType,
        selectedItems.map((item) => Number(item.id))
      );
      setSaveMessage(
        response?.autoAddedModuleCount
          ? i18next.t("labels.content_selection_saved_with_modules", {
              count: response.autoAddedModuleCount,
            })
          : i18next.t("labels.content_selection_saved")
      );
    } catch (saveError) {
      setSaveError(
        saveError.response?.data?.message ||
          i18next.t("labels.content_selection_save_failed")
      );
    } finally {
      setSaving(false);
    }
  };

  if (!enabled) {
    return (
      <div className={css.page}>
        <PageHeader title={i18next.t("labels.content")} />
        <PageBody padding="32px">
          <div className={css.message}>{i18next.t("labels.content_not_enabled")}</div>
        </PageBody>
      </div>
    );
  }

  const title = i18next.t(`labels.${content.label}`);
  const renderList = (items, listType, itemSelection, listTitle) => (
    <section
      className={css.transferColumn}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const itemId = Number(event.dataTransfer.getData("text/plain"));
        if (Number.isSafeInteger(itemId)) moveDraggedItem(itemId, listType);
      }}
      aria-label={listTitle}
    >
      <header className={css.columnHeader}>
        <h2>{listTitle}</h2>
        <span>
          {listType === "available"
            ? `${items.length} / ${availableItems.length}`
            : items.length}
        </span>
      </header>
      {listType === "available" && (
        <div className={css.search}>
          <input
            type="search"
            value={availableSearch}
            onChange={(event) => setAvailableSearch(event.target.value)}
            placeholder={i18next.t("labels.search_content")}
            aria-label={i18next.t("labels.search_content")}
          />
        </div>
      )}
      <div className={css.items}>
        {items.length === 0 ? (
          <div className={css.emptyList}>{i18next.t("labels.no_content_items")}</div>
        ) : (
          items.map((item) => (
            <label
              className={css.transferItem}
              key={item.id}
              draggable={isAdmin && !saving}
              onDragStart={(event) =>
                event.dataTransfer.setData("text/plain", String(item.id))
              }
            >
              {isAdmin && (
                <input
                  type="checkbox"
                  disabled={saving}
                  checked={itemSelection.has(Number(item.id))}
                  onChange={(event) => {
                    const updateIds =
                      listType === "available"
                        ? setSelectedAvailableIds
                        : setSelectedTenantIds;
                    updateIds((ids) =>
                      event.target.checked
                        ? [...ids, Number(item.id)]
                        : ids.filter((id) => id !== Number(item.id))
                    );
                  }}
                  aria-label={item.name}
                />
              )}
              <span>{item.name}</span>
            </label>
          ))
        )}
      </div>
    </section>
  );

  return (
    <div className={css.page}>
      <PageHeader title={title} />
      <PageBody padding="32px" className={css.transferBody}>
        {loading ? (
          <div className={css.message}>{i18next.t("labels.loading_content")}</div>
        ) : error ? (
          <div className={css.error} role="alert">{error}</div>
        ) : (
          <>
            {isAdmin && (
              <div className={css.toolbar}>
                <span>{i18next.t("labels.content_selection_help")}</span>
                <button type="button" onClick={handleSave} disabled={saving}>
                  {saving
                    ? i18next.t("labels.content_selection_saving")
                    : i18next.t("buttons.save_changes")}
                </button>
              </div>
            )}
            {saveError && <div className={css.error} role="alert">{saveError}</div>}
            {saveMessage && <div className={css.success} role="status">{saveMessage}</div>}
            <div
              className={`${css.transferLists} ${
                !isAdmin ? css.readOnlyLists : ""
              }`}
            >
              {renderList(
                filteredAvailableItems,
                "available",
                availableSelection,
                i18next.t(`labels.all_${content.label}`)
              )}
              {isAdmin && (
                <div className={css.moveControls}>
                  <button
                    type="button"
                    aria-label={i18next.t("labels.move_content_to_selected")}
                    disabled={saving || selectedAvailableIds.length === 0}
                    onClick={() => moveItems("available", selectedAvailableIds)}
                  >
                    →
                  </button>
                  <button
                    type="button"
                    aria-label={i18next.t("labels.move_content_to_available")}
                    disabled={saving || selectedTenantIds.length === 0}
                    onClick={() => moveItems("selected", selectedTenantIds)}
                  >
                    ←
                  </button>
                </div>
              )}
              {renderList(
                selectedItems,
                "selected",
                tenantSelection,
                i18next.t(`labels.${content.label}_selected`)
              )}
            </div>
          </>
        )}
      </PageBody>
    </div>
  );
}
